import React, { Suspense, lazy } from "react";
import axios from "axios";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes, useLocation, useParams } from "react-router-dom";
import "@/styles/global.css";
import AuthContextProvider from "@/contexts/authContext";
import MainContextProvider from "@/contexts/mainContext";
import { installLandingAxiosContext } from "@/services/api/landingContext";
import { LoadingOverlay } from "@/components/utils/loader";

// Las APIs que todavía usan la instancia global de Axios también deben tener
// un límite para que una petición caída no deje el loader activo para siempre.
axios.defaults.timeout = 20000;
installLandingAxiosContext();

const page = (loader: () => Promise<{ default: React.ComponentType<any> }>) => lazy(loader);
const LandingRoute = page(() => import("@/pages/LandingRoute"));
const Home = page(() => import("@/pages/index"));
const Login = page(() => import("@/pages/auth/login"));
const Register = page(() => import("@/pages/auth/register"));
const Restore = page(() => import("@/pages/auth/restorepassword"));
const Share = page(() => import("@/pages/share"));
const Notifications = page(() => import("@/pages/notifications"));
const Tournaments = page(() => import("@/pages/tournaments"));
const TournamentCreate = page(() => import("@/pages/tournaments/create"));
const TournamentEdit = page(() => import("@/pages/tournaments/edit/[id]"));
const Tournament = page(() => import("@/pages/tournaments/[id]/index"));
const TournamentTeams = page(() => import("@/pages/tournaments/[id]/teams/index"));
const TournamentTeamDetail = page(() => import("@/pages/tournaments/[id]/teams/[idteam]/index"));
const TournamentGroups = page(() => import("@/pages/tournaments/[id]/groups"));
const TournamentDate = page(() => import("@/pages/tournaments/[id]/date/index"));
const TournamentStandings = page(() => import("@/pages/tournaments/[id]/standings/index"));
const TournamentStatistics = page(() => import("@/pages/tournaments/[id]/statistics/index"));
const TournamentMatches = page(() => import("@/pages/tournaments/[id]/match/index"));
const TournamentMatchDetail = page(() => import("@/pages/tournaments/[id]/match/[idmatch]/index"));
const Challenges = page(() => import("@/pages/challenges"));
const ChallengeCreate = page(() => import("@/pages/challenges/create"));
const ChallengeEdit = page(() => import("@/pages/challenges/edit/[id]"));
const Challenge = page(() => import("@/pages/challenges/[id]"));
const Initiatives = page(() => import("@/pages/initiatives"));
const InitiativeCreate = page(() => import("@/pages/initiatives/create"));
const InitiativeCategory = page(() => import("@/pages/initiatives/categories/[id]/index"));
const Initiative = page(() => import("@/pages/initiatives/[id]/index"));
const Meetpoints = page(() => import("@/pages/meetpoints"));
const Meetpoint = page(() => import("@/pages/meetpoints/[id]/index"));
const Teams = page(() => import("@/pages/teams"));
const Team = page(() => import("@/pages/teams/[id]/index"));
const People = page(() => import("@/pages/people"));
const Market = page(() => import("@/pages/market"));
const MarketProduct = page(() => import("@/pages/market/product/[id]"));
const Store = page(() => import("@/pages/store"));
const Participate = page(() => import("@/pages/participate"));
const Memberships = page(() => import("@/pages/memberships"));
const Services = page(() => import("@/pages/services/offering"));
const King = page(() => import("@/pages/king-of-court"));
const KingCreate = page(() => import("@/pages/king-of-court/create"));
const KingDetail = page(() => import("@/pages/king-of-court/[id]/index"));
const Penalties = page(() => import("@/pages/penalty-championship"));
const PenaltyCreate = page(() => import("@/pages/penalty-championship/create"));
const PenaltyDetail = page(() => import("@/pages/penalty-championship/[id]/index"));
const Profile = page(() => import("@/pages/profile"));
const ProfileUser = page(() => import("@/pages/profile/[idusuario]"));
const Help = page(() => import("@/pages/help"));
const Logout = page(() => import("@/pages/logout"));
const Terms = page(() => import("@/pages/term_and_conditions"));
const NotFound = page(() => import("@/pages/404"));

function Page({ component: Component }: { component: React.ComponentType<any> }) {
  const location = useLocation();
  const params = useParams();
  return <Suspense fallback={<LoadingOverlay />}><Component location={location} params={params} /></Suspense>;
}

class RuntimeErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Error de ejecución en Vite", error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <main style={{ minHeight: "100vh", padding: 32, background: "#0b1020", color: "#fff", fontFamily: "system-ui, sans-serif" }}>
        <h1 style={{ marginTop: 0 }}>Error de ejecución</h1>
        <p>La aplicación no pudo cargar esta vista.</p>
        <pre style={{ whiteSpace: "pre-wrap", color: "#ffb4b4", background: "#171d32", padding: 16, borderRadius: 8 }}>
          {this.state.error.message}\n\n{this.state.error.stack}
        </pre>
      </main>
    );
  }
}

function App() {
  return <BrowserRouter>
    <Routes>
      <Route path="/" element={<Page component={Home} />} />
      <Route path="/auth/login" element={<Page component={Login} />} />
      <Route path="/auth/register" element={<Page component={Register} />} />
      <Route path="/auth/restorepassword" element={<Page component={Restore} />} />
      <Route path="/share" element={<Page component={Share} />} />
      <Route path="/notifications" element={<Page component={Notifications} />} />
      <Route path="/tournaments" element={<Page component={Tournaments} />} />
      <Route path="/tournaments/create" element={<Page component={TournamentCreate} />} />
      <Route path="/tournaments/edit/:id" element={<Page component={TournamentEdit} />} />
      <Route path="/tournaments/:id/teams/:idteam" element={<Page component={TournamentTeamDetail} />} />
      <Route path="/tournaments/:id/teams" element={<Page component={TournamentTeams} />} />
      <Route path="/tournaments/:id/groups" element={<Page component={TournamentGroups} />} />
      <Route path="/tournaments/:id/date" element={<Page component={TournamentDate} />} />
      <Route path="/tournaments/:id/standings" element={<Page component={TournamentStandings} />} />
      <Route path="/tournaments/:id/statistics" element={<Page component={TournamentStatistics} />} />
      <Route path="/tournaments/:id/match/:idmatch" element={<Page component={TournamentMatchDetail} />} />
      <Route path="/tournaments/:id/match" element={<Page component={TournamentMatches} />} />
      <Route path="/tournaments/:id/*" element={<Page component={Tournament} />} />
      <Route path="/challenges" element={<Page component={Challenges} />} />
      <Route path="/challenges/create" element={<Page component={ChallengeCreate} />} />
      <Route path="/challenges/edit/:id" element={<Page component={ChallengeEdit} />} />
      <Route path="/challenges/:id" element={<Page component={Challenge} />} />
      <Route path="/initiatives" element={<Page component={Initiatives} />} />
      <Route path="/initiatives/create" element={<Page component={InitiativeCreate} />} />
      <Route path="/initiatives/categories/:id" element={<Page component={InitiativeCategory} />} />
      <Route path="/initiatives/:id/*" element={<Page component={Initiative} />} />
      <Route path="/meetpoints" element={<Page component={Meetpoints} />} />
      <Route path="/meetpoints/:id" element={<Page component={Meetpoint} />} />
      <Route path="/teams" element={<Page component={Teams} />} />
      <Route path="/teams/:id" element={<Page component={Team} />} />
      <Route path="/people" element={<Page component={People} />} />
      <Route path="/market/product/:id" element={<Page component={MarketProduct} />} />
      <Route path="/market/*" element={<Page component={Market} />} />
      <Route path="/store" element={<Page component={Store} />} />
      <Route path="/participate" element={<Page component={Participate} />} />
      <Route path="/memberships" element={<Page component={Memberships} />} />
      <Route path="/services/offering" element={<Page component={Services} />} />
      <Route path="/king-of-court/create" element={<Page component={KingCreate} />} />
      <Route path="/king-of-court/edit/:id" element={<Page component={KingDetail} />} />
      <Route path="/king-of-court/:id/edit" element={<Page component={KingDetail} />} />
      <Route path="/king-of-court/:id" element={<Page component={KingDetail} />} />
      <Route path="/king-of-court" element={<Page component={King} />} />
      <Route path="/penalty-championship/create" element={<Page component={PenaltyCreate} />} />
      <Route path="/penalty-championship/edit/:id" element={<Page component={PenaltyDetail} />} />
      <Route path="/penalty-championship/:id/edit" element={<Page component={PenaltyDetail} />} />
      <Route path="/penalty-championship/:id" element={<Page component={PenaltyDetail} />} />
      <Route path="/penalty-championship" element={<Page component={Penalties} />} />
      <Route path="/profile/:idusuario" element={<Page component={ProfileUser} />} />
      <Route path="/profile/*" element={<Page component={Profile} />} />
      <Route path="/help" element={<Page component={Help} />} />
      <Route path="/logout" element={<Page component={Logout} />} />
      <Route path="/term_and_conditions" element={<Page component={Terms} />} />
      <Route path="/:landing/*" element={<Page component={LandingRoute} />} />
      <Route path="*" element={<Page component={NotFound} />} />
    </Routes>
  </BrowserRouter>;
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode><RuntimeErrorBoundary><AuthContextProvider><MainContextProvider><App /></MainContextProvider></AuthContextProvider></RuntimeErrorBoundary></React.StrictMode>
);
